import { generateTempAccountCode } from './accountCode';
import { logAccountCreated } from './accountActivityLog';
import { logPortalActivityQuiet } from './portalActivityLog';
import { saveClientRecord } from './saveClient';
import { normalizeStoredPhone } from './intlPhone';
import { formatSupabaseError } from './formatSupabaseError';
import { resolveDistrictFromAddress } from '../utils/singaporeDistrict';
import type { PortalKind } from './portal';
import type { Client } from '../types/crm';
import { idsMatch } from '../utils/crmHelpers';

export type ProspectAccountResult = {
  clientId: string;
  code: string;
  companyName: string;
  created: boolean;
};

/**
 * Create (or reuse) a CRM account for a task "New customer".
 * Uses TEMP-… code + the actor's salesperson code — same rules as Accounts → New account.
 */
export async function ensureProspectAccount(input: {
  companyName: string;
  salespersonCode: string;
  address?: string | null;
  phone?: string | null;
  existingClients: Client[];
  portal: PortalKind;
}): Promise<ProspectAccountResult> {
  const name = input.companyName.trim();
  if (!name) throw new Error('Company name is required.');

  const sp = input.salespersonCode.trim();
  if (!sp) {
    throw new Error(
      'No salesperson code on your profile. Ask Admin Hub to assign a salesperson code before creating accounts.',
    );
  }

  const existing = input.existingClients.find(
    (c) => c.company_name.trim().toLowerCase() === name.toLowerCase(),
  );
  if (existing) {
    return {
      clientId: String(existing.id),
      code: existing.code,
      companyName: existing.company_name,
      created: false,
    };
  }

  const code = generateTempAccountCode();
  const address = input.address?.trim() ?? '';
  const { error, clientId } = await saveClientRecord(
    {
      company_name: name,
      code,
      client_type: 'Potential Customer',
      main_phone: normalizeStoredPhone(input.phone),
      website: '',
      uen: '',
      description: '',
      address,
      district: resolveDistrictFromAddress(address),
    },
    { newSalespersonCode: sp },
  );
  if (error) throw new Error(formatSupabaseError(error));
  if (!clientId) throw new Error('Account was created but no id was returned.');

  try {
    await logAccountCreated(clientId, name);
    logPortalActivityQuiet({
      portal: input.portal,
      action: 'create',
      entityType: 'account',
      entityId: clientId,
      clientId,
      title: `Account created: ${name}`,
      detail: `Code ${code}. SP ${sp}. Created from task (new customer).`,
    });
  } catch {
    // Account exists; activity log is best-effort.
  }

  return { clientId, code, companyName: name, created: true };
}

export function findClientById(clients: Client[], id: string): Client | undefined {
  return clients.find((c) => idsMatch(c.id, id));
}
